import React, {useEffect, useRef} from 'react';
import {animateScroll as scroll} from 'react-scroll'
import {Field, FieldState} from '../types';
import {Tooltip} from './Tooltip';

export type FieldContainerProps = {
    field: Field;
    fieldState: FieldState,
    children: any;
    visible: boolean;
    editableHighlight: boolean;
    displayOnly: boolean;
    displayFieldLabels?: boolean;
};

export const FieldContainer: React.FunctionComponent<FieldContainerProps> = ({
                                                                                 field,
                                                                                 fieldState,
                                                                                 children,
                                                                                 visible,
                                                                                 editableHighlight = false,
                                                                                 displayOnly = false,
                                                                                 displayFieldLabels = true,
                                                                                 locale = 'en-US'
                                                                             }): JSX.Element => {

    const ref = useRef<HTMLDivElement>(null);

    // if fieldState indicates we should scroll to this field, do it....
    useEffect(() => {
        if (fieldState.scrollTo) {
            if (ref?.current?.offsetTop) {
                scroll.scrollTo(ref.current.offsetTop);
            }
        }
    }, [fieldState.scrollTo]);

    // temporary hack until data is fixed
    if (field.cssClass) {
        field.cssClass = field.cssClass.replace(/display-only/, '');
    }
    const {
        touched,
        pristine,
        validating,
        valid,
        invalid,
    } = fieldState;

    const classNames = [
        'field-container',
        field.cssClass && field.cssClass,
        /* field.visibility === 'hidden' && 'hidden', */
        !visible && 'hidden',
        `field-${field.type}`,
        editableHighlight && 'highlight-editable',
        displayOnly && 'display-only',

        touched && 'control-is-touched',
        pristine && 'control-is-pristine',
        validating && 'control-is-validating',
        valid && 'control-is-valid',
        invalid && 'control-is-invalid',
    ]
        .filter(Boolean)
        .join(' ');

    const styles = field?.flowCustomDisplayComponentConfig?.styles || {};
    const innerContainerStyles = field?.flowCustomDisplayComponentConfig?.innerContainer?.styles || {};

    const fieldLabel = (
        field.hasOwnProperty('translations') &&
        field.translations.hasOwnProperty(locale) &&
        field.translations[locale].hasOwnProperty('label') &&
        field.translations[locale].label
    ) ? field.translations[locale].label : field.label;

    const fieldDescription = (
        field.hasOwnProperty('translations') &&
        field.translations.hasOwnProperty(locale) &&
        field.translations[locale].hasOwnProperty('label') &&
        field.translations[locale].description
    ) ? field.translations[locale].description : field.description;

    return (
        <div className={classNames} style={styles}>
            <div className={`inner-container f-${field.id}`} style={innerContainerStyles}>
                {!['html', 'section'].includes(field.type) && displayFieldLabels &&
                    <div ref={ref}>
                        <label className="control-label mb-1">
                            {fieldLabel}
                            {field.isRequired && !displayOnly && <span className="text-danger">*</span>}
                            {field.flowHelpText &&
                                <Tooltip text={field.flowHelpText}>
                                    <a href="#" className="flow-help" title="Help"><i className="fas fa-question-circle"/></a>
                                </Tooltip>
                            }
                        </label>
                        {(!displayOnly || field?.flowCustomDisplayComponentConfig?.alwaysShowFieldDescription === true) && fieldDescription &&
                            <div className="field-description small" dangerouslySetInnerHTML={{__html: fieldDescription}}/>}
                    </div>
                }
                <div>
                    {children}
                </div>
                {['html', 'section'].includes(field.type) && fieldDescription &&
                    <div className="field-description small">{fieldDescription}</div>
                }
            </div>
        </div>
    );
};